import { useEffect, useMemo, useState, useCallback } from 'react';

import { Box, Button, Card, Grid, Paper, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import { useModal } from 'react-modal-hook';

import { ReactComponent as EmptyDarkImage } from 'assets/images/empty-dark.svg';
import { ReactComponent as EmptyLightImage } from 'assets/images/empty-light.svg';
import { ReactComponent as IconSensores } from 'assets/images/sensors.svg';
import { MentionItem } from 'components/ChatInput/mentionSuggestion';
import EmptyState from 'components/EmptyState';
// import InputFormMessage from 'components/InputFormMessage';
import InputFormMessageWithMentions from 'components/InputFormMessageWithMentions';
import ModalAddContato from 'components/ModalAddContato';
import ModalEditarMensagem from 'components/ModalEditarMensagem';
import ModalHistoricoMensagemEditada from 'components/ModalHistoricoMensagemEditada';
import ModalIniciarContato from 'components/ModalIniciarContato';
import NewMessagesContainer from 'components/NewMessagesContainer';
import PasteOrDragAndDropFiles from 'components/PasteOrDragAndDropFiles';
import PesquisarMensagensContainer from 'components/PesquisarMensagensContainer';
import * as emitterEvents from 'constants/emitterEvents';
import {
  LOCAL_STORAGE_KEY_INPUT_GRUPO,
  LOCAL_STORAGE_KEY_INPUT_MENTIONS_GRUPO,
} from 'constants/localStorageKeys';
import * as socketEvents from 'constants/socketEvents';
import { FileWithCaption } from 'hooks/useFileManager';
import Emitter from 'services/eventEmitter';
import socket from 'services/socket';
import { useAppDispatch, useAppSelector } from 'store';
import {
  ContatoInterface,
  fetchContatoAtendimento,
} from 'store/reducers/contatos';
import {
  apagarMensagemGrupo,
  editarMensagemGrupo,
  fetchMarcarGrupoComoLido,
  fetchContatosParaMencao,
  gruposSelector,
  setTotalMensagensNaoLidasGrupo,
} from 'store/reducers/grupos';
import {
  addMensagemGrupo,
  atualizarMensagemEditadaGrupo,
  AtualizarMensagemEditadaGrupoInterface,
  AtualizarMidiaMensagemInterface,
  atualizarMidiaMensagensGrupo,
  atualizarReacoesMensagemGrupo,
  AtualizarReacoesMensagemGrupoInterface,
  atualizarStatusMensagensGrupo,
  atualizarTranscricaoAudioGrupo,
  BaixandoMidiaMensagemInterface,
  baixandoMidiaMensagensGrupo,
  enviarArquivoGrupo,
  fetchMensagensByGrupo,
  MensagemInterface,
  mensagensSelector,
  resetGrupoCursorAConsumir,
  resetIdMensagemPesquisada,
  resetMensagensGrupos,
  resetReplyMessage,
  resetSearchQuery,
  setAfterCursorAConsumir,
  setBeforeCursorAConsumir,
  setLoadingEnviarArquivo,
  setMensagemApagadaGrupo,
  setReplyMessage,
  StatusMensagemType,
} from 'store/reducers/mensagens';
import { ContatoMencionado } from 'types';
import has15MinutesPassed from 'utils/has15MinutesPassed';
import { incluirMencaoTodos } from 'utils/mencaoTodos';

import ListaGrupos from './components/ListaGrupos';
import ModalSeletorCanal from './components/ModalSeletorCanal';
import TopContentAppBar from './components/TopContentAppBar';

const useStyles = makeStyles(({ palette }) => ({
  root: {
    display: 'flex',
    position: 'relative',
    height: '100%',
  },
  svgIcon: {
    color: palette.getContrastText(palette.primary.main),
    width: 24,
    height: 24,
  },
}));

const SEND_TEXT_TIMEOUT_MS = 15000;
const LIMIT_CONTATOS_MENCAO = 10;

export default function Grupos() {
  const classes = useStyles();
  const theme = useTheme();

  const [loadedGrupoId, setLoadedGrupoId] = useState<string | undefined>();
  const [mensagemId, setMensagemId] = useState<string | undefined>();
  const [pesquisarMensagens, setPesquisarMensagens] = useState(false);
  const [filesSelected, setFilesSelected] = useState<File[]>([]);
  const [mensagemASerEditada, setMensagemASerEditada] = useState<
    MensagemInterface | undefined
  >();
  const [contatoSelected, setContatoSelected] = useState<
    ContatoInterface | undefined
  >();

  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useAppDispatch();
  const {
    grupos,
    grupoSelecionado,
    canalSelecionadoId,
    loadingGrupoSelecionado,
    loadingGrupos,
  } = useAppSelector(gruposSelector);
  const {
    mensagensGrupo,
    mensagensGrupoCursor,
    grupoCursorAConsumir,
    loadingMensagensGrupo,
    mensagemIdPesquisada,
    replyMessage,
    searchQuery,
  } = useAppSelector(mensagensSelector);

  const fetchUsers = useCallback(
    async (query: string): Promise<MentionItem[]> => {
      const grupoChatId = grupoSelecionado?.id;
      if (!grupoChatId) return [];

      try {
        const contatos = await dispatch(
          fetchContatosParaMencao({
            grupoChatId,
            limit: LIMIT_CONTATOS_MENCAO,
            filter: query,
          }),
        ).unwrap();
        return incluirMencaoTodos(
          query,
          contatos.map(c => ({
            id: c.id,
            label: c.nome,
            waPhoneNumberId: c.waPhoneNumberId ?? '',
            contact: c.contato,
            foto: c.foto,
          })),
        );
      } catch (error) {
        enqueueSnackbar('Ocorreu um erro ao buscar contatos. Tente novamente.');
        return [];
      }
    },
    [dispatch, enqueueSnackbar, grupoSelecionado?.id],
  );

  const totalGrupos = useMemo(() => grupos.length ?? 0, [grupos.length]);

  const grupoChatCanais =
    grupoSelecionado?.grupoChatCanais.filter(canal => canal.isAtivo) ?? [];

  const totalMsgsNaoLidas = useMemo(() => {
    return grupoSelecionado?.totalMensagensNaoLidas ?? 0;
  }, [grupoSelecionado]);

  const handleSelectFiles = (files: File[]) => {
    setFilesSelected(files);
  };

  /**
   * Emite um evento para o componente de mensagens
   * avisando que uma nova mensagem foi enviada pelo [atendente]
   */
  const sendMessageAtendenteNoScrollTop = (value: boolean) => {
    Emitter.emit(
      emitterEvents.ENVIAR_NOVA_MENSAGEM_ATENDENTE_NO_SCROLL_TOP,
      value,
    );
  };

  /** Send TextMessage v1 */
  // const handleSendText = (
  //   texto: string,
  //   callback: (isSuccess: boolean) => void,
  // ) => {
  //   const grupoChatId = grupoSelecionado?.id;
  //   const mensagemCitadaId = replyMessage?.id;

  //   if (grupoChatId) {
  //     const data = {
  //       grupoChatId,
  //       mensagem: texto,
  //       mensagemCitadaId,
  //       canalId: canalSelecionadoId,
  //     };

  //     const timeoutRef = setTimeout(() => {
  //       callback(false);
  //     }, SEND_TEXT_TIMEOUT_MS);

  //     socket.emit(
  //       socketEvents.EMIT_ENVIAR_MENSAGEM_GRUPO,
  //       data,
  //       (msgEnviada: MensagemInterface) => {
  //         callback(true);
  //         clearTimeout(timeoutRef);
  //         onResetReplyMessage();
  //         dispatch(addMensagemGrupo(msgEnviada));
  //         sendMessageAtendenteNoScrollTop(true);
  //       },
  //     );
  //   }
  // };

  /** Send TextMessage */
  const handleSendText = (
    texto: string,
    contatosMencionados: ContatoMencionado[],
    callback: (isSuccess: boolean) => void,
  ) => {
    const grupoChatId = grupoSelecionado?.id;
    const mensagemCitadaId = replyMessage?.id;

    if (grupoChatId) {
      const data: Record<string, unknown> = {
        grupoChatId,
        mensagem: texto,
        mensagemCitadaId,
        canalId: canalSelecionadoId,
      };

      if (contatosMencionados.length > 0) {
        data.contatosMencionados = contatosMencionados;
      }

      const timeoutRef = setTimeout(() => {
        callback(false);
      }, SEND_TEXT_TIMEOUT_MS);

      socket.emit(
        socketEvents.EMIT_ENVIAR_MENSAGEM_GRUPO,
        data,
        (msgEnviada: MensagemInterface) => {
          callback(true);
          clearTimeout(timeoutRef);
          onResetReplyMessage();
          dispatch(addMensagemGrupo(msgEnviada));
          sendMessageAtendenteNoScrollTop(true);
        },
      );
    }
  };

  /** Send ContatoMessage */
  const handleSendContato = (contato: ContatoInterface) => {
    const grupoChatId = grupoSelecionado?.id;
    const mensagemCitadaId = replyMessage?.id;

    if (grupoChatId) {
      const data = {
        grupoChatId,
        tipo: 'CONTATO',
        mensagemContato: contato,
        mensagemCitadaId,
        canalId: canalSelecionadoId,
      };

      socket.emit(
        socketEvents.EMIT_ENVIAR_MENSAGEM_GRUPO,
        data,
        (msgEnviada: MensagemInterface) => {
          onResetReplyMessage();
          dispatch(addMensagemGrupo(msgEnviada));
          sendMessageAtendenteNoScrollTop(true);
        },
      );
    }
  };

  /** Send AudioMessage */
  const handleSendAudio = (arquivo: Blob, duracao: number) => {
    const grupoChatId = grupoSelecionado?.id;
    const mensagemCitadaId = replyMessage?.id;

    if (grupoChatId) {
      const data = {
        arquivo,
        grupoChatId,
        duracao,
        isGravacaoAudio: true,
        mensagemCitadaId,
        canalId: canalSelecionadoId,
      };

      dispatch(enviarArquivoGrupo(data))
        .unwrap()
        .then(msgEnviada => {
          onResetReplyMessage();
          dispatch(addMensagemGrupo(msgEnviada));
          sendMessageAtendenteNoScrollTop(true);
        });
    }
  };

  /** Send DocumentoMessage e DragAndDrop files */
  const handleSendFiles = (
    files: FileWithCaption[],
    callback?: (isSuccess: boolean) => void,
  ) => {
    const grupoChatId = grupoSelecionado?.id;
    const mensagemCitadaId = replyMessage?.id;

    dispatch(setLoadingEnviarArquivo(true));

    if (!grupoChatId) {
      if (callback) callback(false);
      return;
    }

    const promises = files.map(fileOption => {
      const data = {
        arquivo: fileOption.file,
        grupoChatId,
        mensagemCitadaId,
        midiaTitulo: fileOption.caption,
        canalId: canalSelecionadoId,
        contatosMencionados: fileOption.contatosMencionados,
      };

      return dispatch(enviarArquivoGrupo(data))
        .unwrap()
        .then(msgEnviada => {
          dispatch(addMensagemGrupo(msgEnviada));
          sendMessageAtendenteNoScrollTop(true);
          return { success: true };
        })
        .catch(error => {
          if (error.response) enqueueSnackbar(error.response.data.message);
          dispatch(setLoadingEnviarArquivo(false));
          return { success: false };
        });
    });

    Promise.allSettled(promises).then(results => {
      const allSuccess = results.every(
        r =>
          r.status === 'fulfilled' &&
          (r as PromiseFulfilledResult<{ success: boolean }>).value?.success,
      );

      onResetReplyMessage();
      if (callback) callback(allSuccess);
      if (allSuccess) {
        setFilesSelected([]);
        localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT_GRUPO);
        localStorage.removeItem(LOCAL_STORAGE_KEY_INPUT_MENTIONS_GRUPO);
        dispatch(setLoadingEnviarArquivo(false));
      }
    });
  };

  const onLoadNextMessages = useCallback(() => {
    const { afterCursor: afterAConsumir } = grupoCursorAConsumir[0] || {};
    const { afterCursor } = mensagensGrupoCursor;

    if (grupoSelecionado) {
      const grupoChatId = grupoSelecionado.id;

      if (mensagemIdPesquisada) {
        if (afterAConsumir) {
          dispatch(
            fetchMensagensByGrupo({
              grupoChatId,
              cursor: { afterCursor: afterAConsumir },
            }),
          )
            .unwrap()
            .then(data => {
              dispatch(
                setAfterCursorAConsumir({
                  afterCursor: data.cursor.afterCursor,
                  type: 'grupo',
                }),
              );
            });
        }
      } else {
        dispatch(
          fetchMensagensByGrupo({ grupoChatId, cursor: { afterCursor } }),
        );
      }
    }
  }, [
    dispatch,
    grupoCursorAConsumir,
    grupoSelecionado,
    mensagemIdPesquisada,
    mensagensGrupoCursor,
  ]);

  const onLoadPreviousMessages = useCallback(() => {
    const { beforeCursor: beforeAConsumir } = grupoCursorAConsumir[0] || {};

    if (beforeAConsumir && grupoSelecionado && mensagemIdPesquisada) {
      const grupoChatId = grupoSelecionado.id;

      dispatch(
        fetchMensagensByGrupo({
          grupoChatId,
          cursor: { beforeCursor: beforeAConsumir },
        }),
      )
        .unwrap()
        .then(data => {
          dispatch(
            setBeforeCursorAConsumir({
              beforeCursor: data.cursor.beforeCursor,
              type: 'grupo',
            }),
          );
        });
    }
  }, [dispatch, grupoCursorAConsumir, grupoSelecionado, mensagemIdPesquisada]);

  const handleResetLista = useCallback(() => {
    const grupoChatId = grupoSelecionado?.id;

    if (grupoChatId) {
      dispatch(resetIdMensagemPesquisada());
      dispatch(resetGrupoCursorAConsumir());
      dispatch(resetMensagensGrupos());
      dispatch(fetchMensagensByGrupo({ grupoChatId }));
    }
  }, [dispatch, grupoSelecionado?.id]);

  const handleConfirmarLeituraMensagens = (force = false) => {
    const grupoChatId = grupoSelecionado?.id;

    if (
      grupoChatId &&
      (force || totalMsgsNaoLidas > 0 || totalMsgsNaoLidas === -1)
    ) {
      dispatch(fetchMarcarGrupoComoLido(grupoChatId));
      dispatch(setTotalMensagensNaoLidasGrupo({ grupoChatId, total: 0 }));
    }
  };

  const handleReenviarMensagem = (mensagem: MensagemInterface) => {
    const { grupoChatId, id: mensagemId } = mensagem;

    if (grupoChatId) {
      const data = { id: mensagemId, grupoChatId, canalId: canalSelecionadoId };
      socket.emit(socketEvents.EMIT_REENVIAR_MENSAGEM_GRUPO, data);
    }
  };

  const handleResponderMensagem = (mensagem: MensagemInterface) => {
    dispatch(setReplyMessage(mensagem));
  };

  const handleApagarMensagem = (mensagemId: string) => {
    dispatch(apagarMensagemGrupo(mensagemId))
      .unwrap()
      .catch(error => {
        enqueueSnackbar(
          error?.response?.data?.message || 'Erro ao apagar mensagem',
        );
      });
  };

  const onResetReplyMessage = () => {
    dispatch(resetReplyMessage());
  };

  const [showModalEditarMensagem, hideModalEditarMensagem] = useModal(
    ({ in: open }) =>
      mensagemASerEditada ? (
        <ModalEditarMensagem
          open={open}
          onClose={hideModalEditarMensagem}
          mensagem={mensagemASerEditada}
          handleEditarMensagem={handleEditarMensagem}
          fetchUsers={fetchUsers}
        />
      ) : null,
    [mensagemASerEditada?.mensagem],
  );

  const onEditarMensagem = (mensagem: MensagemInterface) => {
    if (has15MinutesPassed(mensagem.criadoEm, new Date())) {
      enqueueSnackbar(
        'As mensagens não podem ser editadas após 15 minutos. ⏳',
      );
      return;
    }

    setMensagemASerEditada(mensagem);
    showModalEditarMensagem();
  };

  const [showModalHistoricoMensagemEditada, hideModalHistoricoMensagemEditada] =
    useModal(
      ({ in: open }) =>
        mensagemId ? (
          <ModalHistoricoMensagemEditada
            open={open}
            onClose={hideModalHistoricoMensagemEditada}
            mensagemId={mensagemId}
          />
        ) : null,
      [mensagemId],
    );

  const handleVerHistoricoMensagemEditada = (mensagemId: string) => {
    setMensagemId(mensagemId);
    showModalHistoricoMensagemEditada();
  };

  const [showModalSeletorCanal, hideModalSeletorCanal] = useModal(
    ({ in: open }) => (
      <ModalSeletorCanal
        open={open}
        onClose={hideModalSeletorCanal}
        canais={grupoChatCanais}
        selectedCanalId={canalSelecionadoId}
      />
    ),
    [grupoChatCanais, canalSelecionadoId],
  );

  const handleEditarMensagem = (
    mensagem: MensagemInterface,
    newText: string,
    contatosMencionados?: ContatoMencionado[],
  ) => {
    dispatch(
      editarMensagemGrupo({
        messageId: mensagem.id,
        newText,
        contatosMencionados,
      }),
    )
      .unwrap()
      .then(() => {
        hideModalEditarMensagem();
        setMensagemASerEditada(undefined);
      })
      .catch(() => {
        enqueueSnackbar('Não foi possível editar a mensagem. Tente novamente.');
      });
  };

  const [showModalAddContato, hideModalAddContato] = useModal(
    ({ in: open }) => (
      <ModalAddContato
        open={open}
        contato={contatoSelected}
        onClose={hideModalAddContato}
      />
    ),
    [contatoSelected],
  );

  const [showModalIniciarContato, hideModalIniciarContato] = useModal(
    ({ in: open }) => (
      <ModalIniciarContato
        open={open}
        contato={contatoSelected}
        onClose={hideModalIniciarContato}
      />
    ),
    [contatoSelected],
  );

  const handleMentionClick = async (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    const target = event.target as HTMLElement;
    const mentionLink = target.closest('a.mention') as HTMLAnchorElement | null;
    if (mentionLink && grupoSelecionado?.id) {
      event.preventDefault();
      const contatoId = mentionLink.getAttribute('data-contato-id');
      const contatoPhoneAttribute =
        mentionLink.getAttribute('data-contato-phone');
      const contatoPhone =
        contatoPhoneAttribute || mentionLink.textContent?.replace(/^@/, '');

      if (contatoId) {
        let contato: ContatoInterface | undefined;

        try {
          const contatos = await dispatch(
            fetchContatosParaMencao({
              grupoChatId: grupoSelecionado.id,
              filter: contatoPhone || undefined,
            }),
          ).unwrap();
          contato = contatos.find(c => c.id === contatoId);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }

        if (!contato) {
          try {
            contato = await dispatch(
              fetchContatoAtendimento(contatoId),
            ).unwrap();
          } catch (error) {
            enqueueSnackbar(
              'Não foi possível carregar as informações do contato.',
              {
                variant: 'error',
              },
            );
            return;
          }
        }

        if (contato) {
          if (contato.tipo === 'PRIVADO') {
            setContatoSelected(contato);
            showModalIniciarContato();
          } else if (contato.tipo === 'ANONIMO') {
            setContatoSelected(contato);
            showModalAddContato();
          }
        }
      }
    }
  };

  // event listeners
  useEffect(() => {
    const grupoChatId = grupoSelecionado?.id;

    if (grupoChatId) {
      // Recarrega as mensagens do atendimento selecionado
      dispatch(resetIdMensagemPesquisada());
      dispatch(resetGrupoCursorAConsumir());
      dispatch(resetSearchQuery());
      dispatch(resetMensagensGrupos());
      dispatch(fetchMensagensByGrupo({ grupoChatId })).finally(() => {
        setLoadedGrupoId(grupoChatId);
      });

      // Observa o recebimento de novas mensagens do contato no atendimento selecionado
      const handlerReceberMensagem = (msgRecebida: MensagemInterface) => {
        // Add nova mensagem se a mesma pertencer ao atendimento selecionado
        if (msgRecebida.grupoChatId === grupoSelecionado?.id) {
          dispatch(addMensagemGrupo(msgRecebida));
        }
      };
      // observa quando uma mensagem é apagada no grupo
      const handlerMensagemApagada = (data: {
        mensagemId: string;
        apagadaEm: string;
      }) => {
        dispatch(setMensagemApagadaGrupo(data));
      };
      // Observa os novos status de recebimento de mensagens no atendimento
      const handlerMensagemStatus = (data: {
        ackStatus: StatusMensagemType;
        ids: string[];
      }) => {
        dispatch(atualizarStatusMensagensGrupo(data));
      };

      // Observa a reação de uma mensagem
      const onReacaoMensagem = (
        data: AtualizarReacoesMensagemGrupoInterface,
      ) => {
        dispatch(atualizarReacoesMensagemGrupo(data));
      };

      const onEdicaoMensagem = (
        data: AtualizarMensagemEditadaGrupoInterface,
      ) => {
        dispatch(atualizarMensagemEditadaGrupo(data));
      };

      // Observa quando uma transcrição de áudio chegar via WebSocket
      const handleTranscricaoAudio = (data: {
        atendimentoId?: string;
        grupoChatId?: string;
        mensagem: { id: string; text: string };
        status: 'completed' | 'failed' | 'requested';
      }) => {
        if (data.grupoChatId === grupoChatId) {
          dispatch(
            atualizarTranscricaoAudioGrupo({
              grupoChatId: data.grupoChatId,
              mensagemId: data.mensagem.id,
              status: data.status,
              text: data.mensagem.text,
            }),
          );
        }
      };

      // Observa a reconexão do socket e recarrega as mensagens do grupo selecionado
      // e envia a confirmacao de leitura das mensagens
      const handlerReconnect = () => {
        if (grupoChatId) {
          dispatch(resetMensagensGrupos());
          dispatch(fetchMensagensByGrupo({ grupoChatId }));
        }
      };

      socket.on(socketEvents.ON_RECEBER_MENSAGEM_GRUPO, handlerReceberMensagem);
      socket.on(socketEvents.ON_MENSAGEM_APAGADA_GRUPO, handlerMensagemApagada);
      socket.on(socketEvents.ON_MENSAGEM_STATUS_GRUPO, handlerMensagemStatus);
      socket.on(socketEvents.ON_REACAO_MENSAGEM, onReacaoMensagem);
      socket.on(socketEvents.ON_EDICAO_MENSAGEM, onEdicaoMensagem);
      socket.on(socketEvents.ON_TRANSCRICAO_AUDIO, handleTranscricaoAudio);
      socket.on(socketEvents.ON_RECONNECT, handlerReconnect);

      return () => {
        socket.off(
          socketEvents.ON_RECEBER_MENSAGEM_GRUPO,
          handlerReceberMensagem,
        );
        socket.off(
          socketEvents.ON_MENSAGEM_APAGADA_GRUPO,
          handlerMensagemApagada,
        );

        socket.off(
          socketEvents.ON_MENSAGEM_STATUS_GRUPO,
          handlerMensagemStatus,
        );

        socket.off(socketEvents.ON_REACAO_MENSAGEM, onReacaoMensagem);
        socket.off(socketEvents.ON_EDICAO_MENSAGEM, onEdicaoMensagem);
        socket.off(socketEvents.ON_TRANSCRICAO_AUDIO, handleTranscricaoAudio);
        socket.off(socketEvents.ON_RECONNECT, handlerReconnect);
      };
    }
    setLoadedGrupoId(undefined);

    return () => null;
  }, [grupoSelecionado?.id, dispatch]);

  useEffect(() => {
    const grupoChatId = grupoSelecionado?.id;

    if (grupoChatId) {
      const baixandoMidiaMensagemGrupo = (
        data: BaixandoMidiaMensagemInterface,
      ) => {
        dispatch(baixandoMidiaMensagensGrupo(data));
      };

      const atualizarMidiaMensagemGrupo = (
        data: AtualizarMidiaMensagemInterface,
      ) => {
        dispatch(atualizarMidiaMensagensGrupo(data));
      };

      socket.on(
        socketEvents.ON_BAIXANDO_MIDIA_MENSAGEM_GRUPO,
        baixandoMidiaMensagemGrupo,
      );
      socket.on(
        socketEvents.ON_ATUALIZAR_MIDIA_MENSAGEM_GRUPO,
        atualizarMidiaMensagemGrupo,
      );

      return () => {
        socket.off(
          socketEvents.ON_BAIXANDO_MIDIA_MENSAGEM_GRUPO,
          baixandoMidiaMensagemGrupo,
        );
        socket.off(
          socketEvents.ON_ATUALIZAR_MIDIA_MENSAGEM_GRUPO,
          atualizarMidiaMensagemGrupo,
        );
      };
    }
    return () => null;
  }, [dispatch, grupoSelecionado?.id]);

  const handleResetInitialFiles = () => {
    setFilesSelected([]);
  };

  const renderFooter = () => {
    if (grupoSelecionado?.status === 'INATIVO') {
      return (
        <Box component={Card} textAlign="center" p={2} mt={1} mb={1}>
          <Typography color="textSecondary" variant="body2">
            Você não pode mais enviar mensagens para este grupo porque você não
            é mais um participante.
          </Typography>
        </Box>
      );
    }

    if (!canalSelecionadoId && grupoChatCanais.length > 1) {
      return (
        <Paper
          elevation={0}
          style={{
            background: 'transparent',
            textAlign: 'center',
            marginTop: 8,
            marginBottom: 8,
          }}
        >
          <Button
            startIcon={<IconSensores className={classes.svgIcon} />}
            size="large"
            fullWidth
            variant="contained"
            color="primary"
            onClick={showModalSeletorCanal}
          >
            Clique aqui para enviar mensagem no grupo
          </Button>
        </Paper>
      );
    }

    return (
      // <InputFormMessage
      //   isGroup
      //   onSendInputText={handleSendText}
      //   onSendContato={handleSendContato}
      //   onSendAudio={handleSendAudio}
      //   onSelectFiles={handleSelectFiles}
      // />
      <InputFormMessageWithMentions
        grupoChatId={grupoSelecionado?.id ?? ''}
        onSendInputText={handleSendText}
        onSendContato={handleSendContato}
        onSendAudio={handleSendAudio}
        onSelectFiles={handleSelectFiles}
      />
    );
  };

  const isInitialLoadingMessages =
    !!grupoSelecionado && grupoSelecionado.id !== loadedGrupoId;

  const showContent =
    !!grupoSelecionado && !isInitialLoadingMessages && !loadingGrupoSelecionado;

  return (
    <Box className={classes.root}>
      <Grid container spacing={1}>
        <ListaGrupos resetSelectedFiles={handleResetInitialFiles} />

        <Grid item xs component={Box} height="100%" position="relative">
          {/* Não tem nenhum grupo na lista */}
          {totalGrupos <= 0 && !loadingGrupos && (
            <EmptyState
              icon={
                theme.palette.type === 'dark' ? (
                  <EmptyDarkImage />
                ) : (
                  <EmptyLightImage />
                )
              }
              title="No momento seu canal não faz parte de nenhum grupo"
              description="Quando houver novos grupos eles aparecerão aqui do lado"
            />
          )}

          {/* Existem grupos na lista mas nenhum foi selecionado ainda e não está carregando nada */}
          {totalGrupos > 0 &&
            !grupoSelecionado &&
            !loadingGrupoSelecionado &&
            !isInitialLoadingMessages && (
              <EmptyState
                icon={
                  theme.palette.type === 'dark' ? (
                    <EmptyDarkImage />
                  ) : (
                    <EmptyLightImage />
                  )
                }
                title="Que tal dar uma olhadinha nos grupos?!"
                description="Selecione um grupo ao lado para ver o que está rolando por lá"
              />
            )}

          {/* Carregando as primeiras mensagens do grupo selecionado */}
          {(isInitialLoadingMessages || loadingGrupoSelecionado) && (
            <EmptyState
              title="Um momento..."
              description="Estamos carregando as mensagens do grupo para você"
            />
          )}

          {showContent && (
            <PasteOrDragAndDropFiles
              initialFiles={filesSelected}
              onSendFiles={handleSendFiles}
              resetInitialFiles={handleResetInitialFiles}
              fetchUsers={fetchUsers}
              localStorageKeyInput={LOCAL_STORAGE_KEY_INPUT_GRUPO}
              localStorageKeyInputMentions={
                LOCAL_STORAGE_KEY_INPUT_MENTIONS_GRUPO
              }
            >
              <TopContentAppBar
                onPesquisarMensagens={() => {
                  setPesquisarMensagens(prev => !prev);
                }}
              />

              {/* <MessagesContainer
                messages={mensagensGrupo}
                next={onLoadNextMessages}
                hasMore={Boolean(mensagensGrupoCursor.afterCursor)}
                loading={loadingMensagensGrupo}
                countMensagensNaoLidas={totalMsgsNaoLidas}
                onScrollToTop={handleConfirmarLeituraMensagens}
                onReenviarMensagem={handleReenviarMensagem}
                onResponderMensagem={handleResponderMensagem}
                endMessage="Este é o início do histórico de mensagens do grupo"
                actions={
                  grupoSelecionado?.status === 'INATIVO' ? false : undefined
                }
                onVerHistoricoMensagemEditada={
                  handleVerHistoricoMensagemEditada
                }
                isGroup
              /> */}

              <Box
                onClick={handleMentionClick}
                style={{ flexGrow: 1, minHeight: 0, position: 'relative' }}
              >
                <NewMessagesContainer
                  messages={mensagensGrupo}
                  next={onLoadNextMessages}
                  previous={onLoadPreviousMessages}
                  hasAfterCursor={
                    mensagemIdPesquisada
                      ? Boolean(grupoCursorAConsumir[0]?.afterCursor)
                      : Boolean(mensagensGrupoCursor.afterCursor)
                  }
                  hasBeforeCursor={
                    mensagemIdPesquisada
                      ? Boolean(grupoCursorAConsumir[0]?.beforeCursor)
                      : Boolean(mensagensGrupoCursor.beforeCursor)
                  }
                  loading={loadingMensagensGrupo}
                  countMensagensNaoLidas={totalMsgsNaoLidas}
                  onScrollToTop={handleConfirmarLeituraMensagens}
                  onReenviarMensagem={handleReenviarMensagem}
                  onResponderMensagem={handleResponderMensagem}
                  onApagarMensagem={handleApagarMensagem}
                  onEditarMensagem={onEditarMensagem}
                  endMessage="Este é o início do histórico de mensagens do grupo"
                  actions={
                    grupoSelecionado?.status === 'INATIVO' ||
                    canalSelecionadoId === undefined
                      ? false
                      : undefined
                  }
                  onVerHistoricoMensagemEditada={
                    handleVerHistoricoMensagemEditada
                  }
                  onResetLista={handleResetLista}
                  searchQuery={pesquisarMensagens ? searchQuery : ''}
                  isGroup
                />
              </Box>

              {renderFooter()}
            </PasteOrDragAndDropFiles>
          )}
        </Grid>

        {grupoSelecionado && (
          <Grid
            item
            xs
            lg={3}
            component={Box}
            height="100%"
            style={{ display: pesquisarMensagens ? 'block' : 'none' }}
          >
            <PesquisarMensagensContainer
              key={grupoSelecionado.id}
              pesquisarMensagens={pesquisarMensagens}
              setPesquisarMensagens={setPesquisarMensagens}
              grupo={grupoSelecionado}
              direction="left"
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
